/**
 * ESTIMATED DELIVERY CALCULATOR - QUICK REFERENCE
 * ==============================================
 * 
 * PURPOSE: Combines delivery times from cart + current product to show total order delivery estimate
 * 
 * HOW IT WORKS:
 * • Gets delivery days from HTML data attributes (data-delivery-from, data-delivery-to)
 * • Calculates min start + max end across all products  
 * • Shows combined range (e.g., "Sep 3 - Sep 8, 2025")
 * 
 * TRIGGERS: Cart changes, variant changes, page load
 * 
 * USAGE:
 * • window.refreshEstimates() - Manual refresh
 * • window.estimatedDelivery.updateEstimatedDelivery() - Cart integration
 * 
 * REQUIREMENTS:
 * • Cart items need: data-delivery-from="2" data-delivery-to="5" 
 * • Display needs: #delivery-date-text element
 * 
 * EXAMPLE: Cart has (2-4 days) + viewing (1-3 days) = shows (1-4 days range)
 */

// =============================================================================
// GLOBAL VARIABLES
// =============================================================================

let deliveryUpdateTimeout;
let isUpdating = false;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if delivery data exists and toggle wrapper visibility accordingly
 * @param {HTMLElement} wrapper - The wrapper element to show/hide
 * @param {boolean} hasData - Whether delivery data exists
 */
function checkAndToggleVisibility(wrapper, hasData) {
  if (!wrapper) return;
  
  if (hasData) {
    wrapper.style.display = 'block';
  } else {
    wrapper.style.display = 'none';
  }
}

/**
 * Debounce updates to reduce excessive calls
 * @param {Function} callback - Function to debounce
 * @param {number} delay - Delay in milliseconds (default: 300)
 */
function debounceUpdate(callback, delay = 300) {
  if (deliveryUpdateTimeout) clearTimeout(deliveryUpdateTimeout);
  deliveryUpdateTimeout = setTimeout(callback, delay);
}

/**
 * Extract delivery start and end days from HTML elements
 * @param {NodeList} elements - Elements with delivery data attributes
 * @returns {Array} Array of delivery objects with start/end days
 */
function getDeliveryDays(elements) {
  let deliveries = [];
  
  for (let element of elements) {
    let start = parseInt(element.dataset.deliveryFrom);
    let end = parseInt(element.dataset.deliveryTo);
    
    // Convert NaN to null, but keep 0 as valid (since Liquid now ensures no 0s)
    if (isNaN(start)) start = null;
    if (isNaN(end)) end = null;
    
    // Since Liquid now provides fallback values, we should always have data
    deliveries.push({
      start: start,
      end: end
    });
  }
  
  return deliveries;
}

/**
 * Calculate overall delivery range across all products
 * @param {Array} deliveries - Array of delivery objects
 * @returns {Object} Object with earliest and latest delivery days
 */
function calculateRange(deliveries) {
  let earliest = null;
  let latest = null;
  
  for (let delivery of deliveries) {
    if (delivery.start !== null) {
      earliest = earliest === null ? delivery.start : Math.max(earliest, delivery.start);
    }
    if (delivery.end !== null) {
      latest = latest === null ? delivery.end : Math.max(latest, delivery.end);
    }
  }
  
  return { earliest, latest };
}

/**
 * Add days to a date
 * @param {Date} date - Base date
 * @param {number} days - Number of days to add
 * @returns {Date} New date with added days
 */
function addDays(date, days) {
  let result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Format date to 'MMM DD, YYYY' format
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string
 */
function formatDate(date) {
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    // year: 'numeric'  
  });
}

/**
 * Display estimated delivery range or single date
 * @param {HTMLElement} element - Element to display the estimate in
 * @param {number|null} start - Start day
 * @param {number|null} end - End day
 */
function showEstimate(element, start, end) {
  if (!element) return;
  
  let today = new Date();
  let text = '';
  
  if (start !== null && end !== null) {
    text = start === end 
      ? formatDate(addDays(today, start)) 
      : `${formatDate(addDays(today, start))} - ${formatDate(addDays(today, end))}`;
  } else if (start !== null) {
    text = formatDate(addDays(today, start));
  } else if (end !== null) {
    text = formatDate(addDays(today, end));
  }
  
  element.innerHTML = `<span class="estimated-delivery-date">${text}</span>`;
}

/**
 * Hide the delivery estimate wrapper
 * @param {HTMLElement} wrapper - Wrapper element to hide
 */
function hideEstimate(wrapper) {
  if (wrapper) wrapper.style.display = 'none';
}

/**
 * Show the delivery estimate wrapper
 * @param {HTMLElement} wrapper - Wrapper element to show
 */
function showEstimateWrapper(wrapper) {
  if (wrapper) wrapper.style.display = 'block';
}

// =============================================================================
// UPDATE FUNCTIONS
// =============================================================================

/**
 * Update delivery estimate displayed in the cart drawer AND cart edit popup
 */
function updateCartEstimate() {
  // Handle both cart drawer AND cart edit popup
  let items = document.querySelectorAll('#CartDrawer .cart-item[data-delivery-from], #CartDrawer .cart-item[data-delivery-to], .cart-edit .cart-item[data-delivery-from], .cart-edit .cart-item[data-delivery-to]');
  
  // Update cart drawer
  let dateElement = document.querySelector('#CartDrawer #delivery-date-text');
  let wrapper = document.querySelector('#CartDrawer #estimated-delivery-wrapper');
  
  // Update cart edit popup  
  let editDateElement = document.querySelector('.cart-edit #delivery-date-text');
  let editWrapper = document.querySelector('.cart-edit #estimated-delivery-wrapper');
  
  if (items.length === 0) {
    checkAndToggleVisibility(wrapper, false);
    checkAndToggleVisibility(editWrapper, false);
    return;
  }

  let deliveries = getDeliveryDays(items);
  if (!deliveries.length) {
    checkAndToggleVisibility(wrapper, false);
    checkAndToggleVisibility(editWrapper, false);
    return;
  }

  let { earliest, latest } = calculateRange(deliveries);
  if (earliest === null && latest === null) {
    checkAndToggleVisibility(wrapper, false);
    checkAndToggleVisibility(editWrapper, false);
    return;
  }

  // Update both locations
  showEstimate(dateElement, earliest, latest);
  showEstimate(editDateElement, earliest, latest);
  checkAndToggleVisibility(wrapper, true);
  checkAndToggleVisibility(editWrapper, true);
}

/**
 * Update delivery estimate on the product page
 */
function updateProductEstimate() {
  // Check both main product page AND modal content (FIXED SELECTORS)
  let wrapper = document.querySelector('.estimated-main-product-delivery-container #estimated-delivery-wrapper') ||
               document.querySelector('.quick-add-modal__content-info #estimated-delivery-wrapper') ||
               document.querySelector('[id^="QuickAddInfo-"] #estimated-delivery-wrapper') ||
               document.querySelector('.cart-edit #estimated-delivery-wrapper');
  
  if (!wrapper) {
    return;
  }

  let start = parseInt(wrapper.dataset.deliveryFrom);
  let end = parseInt(wrapper.dataset.deliveryTo);
  
  start = (isNaN(start) || start === 0) ? null : start;
  end = (isNaN(end) || end === 0) ? null : end;

  if (start === null && end === null) {
    return hideEstimate(wrapper);
  }

  let deliveries = [{ start, end }];
  let items = document.querySelectorAll('#CartDrawer .cart-item[data-delivery-from], #CartDrawer .cart-item[data-delivery-to]');
  
  if (items.length) {
    deliveries = deliveries.concat(getDeliveryDays(items));
  }

  let { earliest, latest } = calculateRange(deliveries);
  if (earliest === null && latest === null) {
    return hideEstimate(wrapper);
  }
  
  // Check both main product page AND modal content for the date element (FIXED SELECTORS)
  let dateElement = document.querySelector('.estimated-main-product-delivery-container #delivery-date-text') ||
                   document.querySelector('.quick-add-modal__content-info #delivery-date-text') ||
                   document.querySelector('[id^="QuickAddInfo-"] #delivery-date-text') ||
                   document.querySelector('.cart-edit #delivery-date-text');
  
  showEstimate(dateElement, earliest, latest);
  showEstimateWrapper(wrapper);
}

/**
 * Update delivery estimate on cart page
 */
function updateCartPageEstimate() {
  let items = document.querySelectorAll('.cart-page-item[data-delivery-from], .cart-page-item[data-delivery-to]');
  let dateElement = document.querySelector('#cart-page-delivery-date-text');
  let wrapper = document.querySelector('#cart-page-estimated-delivery-wrapper');
  
  if (items.length === 0) {
    checkAndToggleVisibility(wrapper, false);
    return;
  }

  let deliveries = getDeliveryDays(items);
  if (!deliveries.length) {
    checkAndToggleVisibility(wrapper, false);
    return;
  }

  let { earliest, latest } = calculateRange(deliveries);
  if (earliest === null && latest === null) {
    checkAndToggleVisibility(wrapper, false);
    return;
  }

  showEstimate(dateElement, earliest, latest);
  checkAndToggleVisibility(wrapper, true);
}

/**
 * Combined update function to refresh all delivery estimates
 */
function updateAllEstimates() {
  if (isUpdating) return;
  
  isUpdating = true;
  updateCartEstimate();        // Cart drawer + Cart edit popup
  updateProductEstimate();     // Product page
  updateCartPageEstimate();    // Cart page
  setTimeout(() => isUpdating = false, 100);
}

// =============================================================================
// EVENT SETUP
// =============================================================================

/**
 * Setup event listeners for cart updates and variant changes
 */
function setupEstimateEvents() {
  function handleUpdate() { 
    debounceUpdate(updateAllEstimates, 300); 
  }
  
  // Initial load
  document.addEventListener('DOMContentLoaded', updateAllEstimates);
  
  // Cart update events
  document.addEventListener('cart:updated', handleUpdate);
  if (window.jQuery) { 
    $(document).on('cart:updated', handleUpdate); 
  }
  
  // Variant change events
  document.addEventListener('change', event => {
    if (event.target.name && event.target.name.includes('id')) {
      debounceUpdate(updateProductEstimate, 200);
    }
  });

  // Add specific modal variant handling
  document.addEventListener('change', event => {
    if (event.target.closest('.quick-add-modal') && event.target.name && event.target.name.includes('id')) {
      debounceUpdate(updateProductEstimate, 200);
    }
  });

  // NEW: Watch for cart edit popup content changes
  const cartEditObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList' && 
          (mutation.target.classList.contains('perparent-prod') || 
           mutation.target.classList.contains('cart-edit-popup-body'))) {
        setTimeout(() => {
          updateAllEstimates();
        }, 200);
      }
    });
  });

  // NEW: Start observing the cart edit popup body
  const cartEditBody = document.querySelector('.cart-edit-popup-body');
  if (cartEditBody) {
    cartEditObserver.observe(cartEditBody, { childList: true, subtree: true });
  }
}

// =============================================================================
// INITIALIZATION & GLOBAL EXPOSURE
// =============================================================================

// Initialize event listeners and initial update
setupEstimateEvents();

// Expose global functions for external calls
window.updateCartEstimate = updateCartEstimate;
window.updateProductEstimate = updateProductEstimate;
window.updateCartPageEstimate = updateCartPageEstimate;
window.refreshEstimates = updateAllEstimates;
window.estimatedDelivery = { 
  updateEstimatedDelivery: updateAllEstimates 
};
